import { useEffect, useRef } from 'react';

/*
 * useDismissable — shared overlay behavior for Modal + Drawer.
 * When `open`:
 *   - locks body scroll,
 *   - closes on Escape (calls onClose),
 *   - moves focus into the panel and returns it to the trigger on close,
 *   - keeps Tab focus inside the panel (focus-trap-ish).
 * Returns a ref to attach to the panel element.
 *
 * Not a full inert()/aria-modal management layer — deliberately light, matching
 * the mock's own drawer (ESC + scrim-click). Sufficient for admin dialogs.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
  'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function useDismissable(open, onClose, { closeOnEsc = true, trapFocus = true } = {}) {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement;

    // lock scroll (preserve the current padding to avoid layout shift)
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // focus the first focusable in the panel, else the panel itself
    const panel = panelRef.current;
    const focusFirst = () => {
      if (!panel) return;
      const first = panel.querySelector(FOCUSABLE);
      (first || panel).focus({ preventScroll: true });
    };
    // defer to next frame so the element is mounted/visible
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (e) => {
      if (closeOnEsc && e.key === 'Escape') {
        e.stopPropagation();
        onClose && onClose('escape');
        return;
      }
      if (trapFocus && e.key === 'Tab' && panel) {
        const nodes = panel.querySelectorAll(FOCUSABLE);
        if (!nodes.length) { e.preventDefault(); return; }
        const list = Array.prototype.slice.call(nodes);
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !panel.contains(active))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      const el = returnFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
    };
  }, [open, onClose, closeOnEsc, trapFocus]);

  return panelRef;
}
